import React from "react";
import { connect } from "react-redux";
import { Redirect, Route, Switch } from "react-router-dom";
import * as router from "react-router-redux";
import _get from "lodash/get";

import CircularProgress from "material-ui-old/CircularProgress";
import GeneralErrorScreen from "../components/GeneralErrorScreen";
import { DEVICES } from "../modules/devices/constants";
import { setPageTitle } from "../modules/title";
import { trackerActions } from "../modules/tracker";
import { api } from "../modules/api";
import {
    isLoading,
    PROGRESS,
    shouldLoadOnUpdate
} from "../modules/asyncHttpAction";
import styles from "../styles/components/Results.css";
import ShareLinkModal from "../components/Results/ShareLinkModal";
import ResultsSideNav from "../components/Results/ResultsSideNav";
import ComparePages from "../components/Results/ComparePages";
import KeyFindings from "../components/Results/KeyFindings";
import LikesAndDislikes from "../components/Results/LikesAndDislikes";
import NewDrivers from "../components/Results/Drivers";
import OldDrivers from "../components/Results/Drivers/index";
import Config from "../config";
import Expectations from "../components/Results/Expectations";
import NextSteps from "../components/Results/NextSteps";
import { denormalize } from "normalizr";
import { wevoResultsSchema, wevoSchema } from "../schemas";
import { resetResultsState } from "../modules/results";

export class ResultsContainer extends React.Component {
    componentWillMount() {
        this.props.fetchWevo();
        this.props.fetchWevoResults();
    }

    componentDidUpdate(prevProps) {
        if (
            shouldLoadOnUpdate(this.props.wevoRequest) ||
            this.props.wevoId !== prevProps.wevoId
        ) {
            this.props.fetchWevo(this.props.wevoId);
        }
        if (
            shouldLoadOnUpdate(this.props.wevoResultsRequest) ||
            this.props.wevoId !== prevProps.wevoId
        ) {
            this.props.fetchWevoResults(this.props.wevoId);
        }

        if (
            this.props.pageTitle &&
            this.props.pageTitle !== prevProps.pageTitle
        ) {
            const deviceId = _get(this.props, ["wevo", "devices", 0, "id"]);
            const deviceIcon = DEVICES[deviceId].icon;
            this.props.setPageTitle(this.props.pageTitle, deviceIcon);
        }

        /* redirect if an authorized team member is viewing a sharelink */
        if (this.props.isShareLink && this.props.teamIsAuthenticated) {
            this.props.navigateTo(
                `/team/${this.props.wevo.ownerTeamId}/results/${
                    this.props.wevo.id
                }`
            );
        }
    }

    componentWillUnmount() {
        this.props.resetResultsState();
    }

    render() {
        if (
            isLoading(this.props.wevoRequest) ||
            isLoading(this.props.wevoResultsRequest)
        ) {
            return (
                <div id="loading-screen" className={styles.nonIdeal}>
                    <CircularProgress />
                </div>
            );
        }

        if (
            this.props.wevoRequest === PROGRESS.error ||
            this.props.wevoResultsRequest === PROGRESS.error ||
            this.props.pageResultsRequest === PROGRESS.error
        ) {
            const error = new Error(`Could not get results`);
            return (
                <div id="error-screen" className={styles.nonIdeal}>
                    <GeneralErrorScreen
                        error={error}
                        redirectLink="/"
                        redirectText="Return to Dashboard"
                    />
                </div>
            );
        }

        return (
            <div id={"results-content"}>
                <ShareLinkModal wevoId={this.props.wevo.id} />
                <nav className={styles.leftNav}>
                    <ResultsSideNav
                        wevo={this.props.wevo}
                        selectedPageId={this.props.selectedPageId}
                        isShareLink={this.props.isShareLink}
                        hasExpectations={this.props.hasExpectations}
                        hasKeyFindings={this.props.hasKeyFindings}
                        resultsBaseUrl={this.props.resultsBaseUrl}
                    />
                </nav>
                <div className={styles.contentWithLeftNav}>
                    <div className={styles.mainContent}>
                        <Switch>
                            {this.props.wevo.pages.length > 1 && (
                                <Route
                                    key="/compare-pages"
                                    exact
                                    path={`${
                                        this.props.resultsBaseUrl
                                    }/compare-pages`}
                                    render={() => (
                                        <ComparePages
                                            wevo={this.props.wevo}
                                            results={this.props.wevoResults}
                                            teamId={this.props.wevo.ownerTeamId}
                                        />
                                    )}
                                />
                            )}

                            {this.props.hasKeyFindings && (
                                <Route
                                    key="/key-findings"
                                    exact
                                    path={`${
                                        this.props.resultsBaseUrl
                                    }/key-findings`}
                                    render={() => (
                                        <KeyFindings
                                            pageId={this.props.selectedPageId}
                                            fetchPageResults={
                                                this.props.fetchPageResults
                                            }
                                        />
                                    )}
                                />
                            )}

                            <Route
                                key="/likes-and-dislikes"
                                exact
                                path={`${
                                    this.props.resultsBaseUrl
                                }/likes-and-dislikes`}
                                render={() => (
                                    <LikesAndDislikes
                                        wevo={this.props.wevo}
                                        pageId={this.props.selectedPageId}
                                        fetchAudiences={
                                            this.props.fetchAudiences
                                        }
                                        fetchPageResults={
                                            this.props.fetchPageResults
                                        }
                                    />
                                )}
                            />

                            <Route
                                key="/drivers"
                                exact
                                path={`${this.props.resultsBaseUrl}/drivers`}
                                render={() => {
                                    return Config.features().newDrivers ? (
                                        <NewDrivers
                                            pageId={this.props.selectedPageId}
                                            fetchPageResults={
                                                this.props.fetchPageResults
                                            }
                                            wevo={this.props.wevo}
                                        />
                                    ) : (
                                        <OldDrivers
                                            pageId={this.props.selectedPageId}
                                            fetchPageResults={
                                                this.props.fetchPageResults
                                            }
                                            wevo={this.props.wevo}
                                        />
                                    );
                                }}
                            />

                            {Config.features().audienceExpectations &&
                                this.props.hasExpectations && (
                                    <Route
                                        key="/expectations"
                                        exact
                                        path={`${
                                            this.props.resultsBaseUrl
                                        }/expectations`}
                                        render={() => (
                                            <Expectations
                                                pageId={
                                                    this.props.selectedPageId
                                                }
                                                fetchPageResults={
                                                    this.props.fetchPageResults
                                                }
                                            />
                                        )}
                                    />
                                )}

                            <Route
                                key="/next-steps"
                                exact
                                path={`${this.props.resultsBaseUrl}/next-steps`}
                                render={() => <NextSteps />}
                            />

                            <Redirect
                                exact
                                path={this.props.resultsBaseUrl}
                                to={this.props.redirectPath}
                            />
                        </Switch>
                    </div>
                </div>
            </div>
        );
    }
}

export function mapStateToProps(state, props) {
    const { queryString, session } = state;

    const {
        wevoId,
        wevoRequest,
        pageResultsRequest,
        wevoResultsRequest
    } = state.results;

    const wevo = denormalize(wevoId, wevoSchema, state.api) || null;
    const wevoResults =
        denormalize(wevoId, wevoResultsSchema, state.api) || null;

    const pageTitle = wevo && wevo.id === wevoId ? wevo.name : null;
    const pages =
        wevo && wevo.id === wevoId && wevo.pages
            ? wevo.pages
                  .filter(page => page.isActive)
                  .sort((a, b) => a.sortOrder - b.sortOrder)
            : null;
    const selectedPageId =
        queryString && queryString.selected
            ? parseInt(queryString.selected, 10)
            : _get(pages, [0, "id"]);

    const hasKeyFindings =
        wevoResults &&
        wevoResults.pages &&
        Object.values(wevoResults.pages).some(page => page.hasKeyFindings);
    const hasExpectations =
        wevoResults &&
        wevoResults.pages &&
        Object.values(wevoResults.pages).some(page => page.hasExpectations);

    const { params } = props.match;
    const shareLinkId = params.shareLinkId || null;
    const resultsBaseUrl = shareLinkId
        ? `/shared/${shareLinkId}`
        : `/team/${params.teamId}/results/${params.wevoId}`;

    let redirectPath;
    if (pages && pages.length > 1) {
        redirectPath = `${resultsBaseUrl}/compare-pages`;
    } else if (hasKeyFindings) {
        redirectPath = `${resultsBaseUrl}/key-findings`;
    } else {
        redirectPath = `${resultsBaseUrl}/likes-and-dislikes`;
    }

    const teamIsAuthenticated =
        !session.authRequired && wevo && session.teamId === wevo.ownerTeamId;

    return {
        teamIsAuthenticated,
        isShareLink: session.isShareLink,
        pageTitle,
        pageResultsRequest,
        selectedPageId,
        hasKeyFindings,
        hasExpectations,
        resultsBaseUrl,
        redirectPath,
        wevo: wevo ? { ...wevo, pages } : null,
        wevoId,
        wevoResults,
        wevoRequest,
        wevoResultsRequest
    };
}

function mapDispatchToProps(dispatch, props) {
    const { wevoId, shareLinkId } = props.match.params;

    const shareLinkProps = {
        fetchWevo: () => dispatch(api.wevoShareLinks.get(shareLinkId)),
        fetchWevoResults: () =>
            dispatch(api.wevoShareLinks.results(shareLinkId)),
        fetchPageResults: pageId =>
            dispatch(api.wevoShareLinks.pages.results(pageId, shareLinkId)),
        fetchAudiences: () => dispatch(api.audiences.list(shareLinkId))
    };

    const wevoProps = {
        fetchWevo: () => dispatch(api.wevos.get(wevoId)),
        fetchWevoResults: () => dispatch(api.wevos.results(wevoId)),
        fetchPageResults: pageId =>
            dispatch(api.wevos.pages.results(pageId, wevoId)),
        fetchAudiences: () => dispatch(api.audiences.list())
    };

    return {
        ...(shareLinkId ? shareLinkProps : wevoProps),
        resetResultsState: () => dispatch(resetResultsState()),
        setPageTitle: (name, icon) => dispatch(setPageTitle(name, icon)),
        navigateTo: route => dispatch(router.push(route)),
        replacePathWith: route => dispatch(router.replace(route)),
        trackEvent: (eventName, eventProperties) =>
            dispatch(trackerActions.trackEvent(eventName, eventProperties))
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ResultsContainer);



// WEBPACK FOOTER //
// ./src/containers/ResultsContainer.js